import { useState, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Room } from '../types'
import type { SendFn } from '../App'

interface RankingProps {
  room: Room
  playerId: string
  send: SendFn
}

const LINE_ID = '__THE_LINE__'

function SortableItem({ id, isLine, isDragging: isBeingDragged }: { id: string; isLine: boolean; isDragging?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 0,
    opacity: isDragging ? 0.5 : 1,
  }
  void isBeingDragged

  if (isLine) {
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div className={`my-2 py-3 px-4 rounded-xl bg-coral/15 border-2 border-dashed border-coral cursor-grab active:cursor-grabbing touch-none select-none ${isDragging ? 'scale-105 bg-coral/25' : 'animate-pulse-glow'}`}>
          <div className="flex items-center justify-center gap-3">
            <div className="flex-1 h-0.5 bg-coral/40" />
            <span className="font-display text-lg font-bold text-coral uppercase tracking-widest whitespace-nowrap">
              DRAG THE LINE
            </span>
            <div className="flex-1 h-0.5 bg-coral/40" />
          </div>
          <div className="flex justify-between mt-1.5 px-1">
            <span className="text-sm font-body text-sage font-bold flex items-center gap-1">
              <span className="text-base">👍</span> Like above
            </span>
            <span className="text-sm font-body text-coral font-bold flex items-center gap-1">
              Dislike below <span className="text-base">👎</span>
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div
        className={`flex items-center gap-3 px-5 py-4 rounded-xl border-2 select-none touch-none cursor-grab active:cursor-grabbing ${
          isDragging
            ? 'bg-coral/10 border-coral shadow-xl'
            : 'bg-white border-warmblack/10 shadow-sm'
        }`}
      >
        <span className="font-body text-xl flex-1 text-warmblack font-bold">{id}</span>
        <span className="text-warmblack/30 text-2xl">&#x2630;</span>
      </div>
    </div>
  )
}

export default function Ranking({ room, playerId, send }: RankingProps) {
  const isRanker = playerId === room.currentRankerId
  const isScribe = playerId === room.scribeId
  const rankerName = room.currentRankerId ? room.players[room.currentRankerId]?.name : '???'
  const scribeName = room.scribeId ? room.players[room.scribeId]?.name : '???'

  const phase = room.phase
  const hasSubmittedIndividual = room.individualSubmitted.includes(playerId)

  // Ranker is rendered into Ranking even during the 'discussion' phase
  // (App.tsx routes them here directly so they skip the discussion screen).
  // Treat them as active in both 'discussion' and 'rankerRanking'.
  const isActiveRanker = ((phase === 'discussion' || phase === 'rankerRanking') && isRanker)
    || (phase === 'groupGuess' && isScribe)
    || (phase === 'individualGuess' && !isRanker && !hasSubmittedIndividual)

  const [items, setItems] = useState<string[]>(() => {
    return [...room.drawnItems, LINE_ID]
  })
  const [locking, setLocking] = useState(false)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragId(null)
    const { active, over } = event
    if (over && active.id !== over.id) {
      setItems((prev) => {
        const oldIndex = prev.indexOf(active.id as string)
        const newIndex = prev.indexOf(over.id as string)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }, [])

  const getRanking = () => {
    const lineIdx = items.indexOf(LINE_ID)
    const ranking = items.filter((i) => i !== LINE_ID)
    return { ranking, lineIndex: lineIdx }
  }

  const lockIn = () => {
    setLocking(true)
    const { ranking, lineIndex } = getRanking()

    if (phase === 'discussion') {
      // We auto-skipped the discussion screen for the ranker; flip the
      // server phase first, then submit. TCP guarantees order, and the
      // server validates phase per message — by the time it processes
      // the second one, phase has been advanced.
      send('start_discussion_to_ranking', undefined)
      send('submit_ranker_ranking', { ranking, lineIndex })
    } else if (phase === 'rankerRanking') {
      send('submit_ranker_ranking', { ranking, lineIndex })
    } else if (phase === 'groupGuess') {
      send('submit_group_guess', { ranking, lineIndex })
    } else if (phase === 'individualGuess') {
      send('submit_individual_guess', { ranking, lineIndex })
      setLocking(false)
    }
  }

  const nonRankerPlayers = Object.keys(room.players).filter((id) => id !== room.currentRankerId)
  const allIndividualsSubmitted = nonRankerPlayers.every((id) => room.individualSubmitted.includes(id))

  // Server auto-advances once every non-ranker has submitted, so the only
  // manual case left is the host skipping ahead while a player is missing.
  const canSkipReveal = phase === 'individualGuess' && playerId === room.hostId && !allIndividualsSubmitted

  const advanceToReveal = () => {
    send('advance_to_reveal', {})
  }

  const title = useMemo(() => {
    if (phase === 'discussion' || phase === 'rankerRanking') {
      return isRanker ? "You're the ranker" : `${rankerName} is ranking...`
    }
    if (phase === 'groupGuess') {
      return isScribe ? "Enter the group's guess" : `${scribeName} is entering the group guess...`
    }
    if (phase === 'individualGuess') {
      if (isRanker) return allIndividualsSubmitted ? "All guesses are in!" : "Waiting for guesses..."
      if (hasSubmittedIndividual) return "Guess submitted!"
      return "Now rank YOUR guess"
    }
    return ''
  }, [phase, isRanker, isScribe, rankerName, scribeName, hasSubmittedIndividual, allIndividualsSubmitted])

  const subtitle = useMemo(() => {
    if (phase === 'discussion' || phase === 'rankerRanking') {
      return isRanker
        ? 'Drag items to reorder. Drag THE LINE to separate positive from negative.'
        : "They're deciding their ranking and where the line goes..."
    }
    if (phase === 'groupGuess') {
      return isScribe
        ? "How does the group think " + rankerName + " ranked these? Drag to order + place the line."
        : "The group is deciding their consensus guess..."
    }
    if (phase === 'individualGuess') {
      if (isRanker) return "Everyone else is submitting their individual guesses."
      if (hasSubmittedIndividual) return "Waiting for others..."
      return "How do YOU think " + rankerName + " ranked these? Place the line too!"
    }
    return ''
  }, [phase, isRanker, isScribe, rankerName, hasSubmittedIndividual])

  if (!isActiveRanker && phase !== 'individualGuess') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="text-7xl mb-6"
        >
          {phase === 'rankerRanking' ? '🤔' : '🗳️'}
        </motion.div>
        <h2 className="font-display text-3xl font-bold text-warmblack mb-3 text-center">
          {title}
        </h2>
        <p className="font-body text-lg text-warmblack/60 text-center max-w-xs">
          {subtitle}
        </p>
        <div className="mt-8 flex flex-wrap gap-2 justify-center max-w-sm">
          {room.drawnItems.map((item) => (
            <span
              key={item}
              className="bg-white px-5 py-2.5 rounded-full font-body text-base shadow-sm border border-warmblack/5"
            >
              {item}
            </span>
          ))}
        </div>
        <p className="mt-8 text-warmblack/40 font-body text-base">Round {room.roundNumber}</p>
      </div>
    )
  }

  if (phase === 'individualGuess' && (isRanker || hasSubmittedIndividual)) {
    const submittedCount = room.individualSubmitted.length
    const totalNeeded = nonRankerPlayers.length
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="text-7xl mb-6"
        >
          {hasSubmittedIndividual ? '✅' : '⏳'}
        </motion.div>
        <h2 className="font-display text-3xl font-bold text-warmblack mb-3 text-center">
          {title}
        </h2>
        <p className="font-body text-lg text-warmblack/60 text-center mb-3">
          {submittedCount} of {totalNeeded} guesses in
        </p>
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {nonRankerPlayers.map((pid) => {
            const submitted = room.individualSubmitted.includes(pid)
            const name = room.players[pid]?.name || '???'
            return (
              <span
                key={pid}
                className={`px-4 py-1.5 rounded-full font-body text-sm font-bold ${
                  submitted
                    ? 'bg-sage/30 text-warmblack line-through'
                    : 'bg-sunflower/30 text-warmblack animate-pulse'
                }`}
              >
                {submitted ? '✓ ' : ''}{name}
              </span>
            )
          })}
        </div>
        {canSkipReveal && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileTap={{ scale: 0.95 }}
            onClick={advanceToReveal}
            className="bg-coral hover:bg-coral/90 text-white font-display font-bold text-2xl py-5 px-10 rounded-2xl shadow-lg transition-all animate-pulse-glow"
          >
            Skip & Reveal
          </motion.button>
        )}
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col px-4 py-6 max-w-lg mx-auto w-full">
      <div className="text-center mb-5">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-warmblack">
          {title}
        </h2>
        <p className="font-body text-base text-warmblack/60 mt-1">
          {subtitle}
        </p>
      </div>

      <div className="flex-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-2.5">
              {items.map((item) => (
                <SortableItem
                  key={item}
                  id={item}
                  isLine={item === LINE_ID}
                  isDragging={activeDragId === item}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={lockIn}
        disabled={locking}
        className="mt-6 bg-coral hover:bg-coral/90 disabled:opacity-50 text-white font-display font-bold text-2xl py-5 rounded-2xl shadow-lg transition-all animate-pulse-glow"
      >
        {locking ? 'Locking in...' : 'Lock It In 🔒'}
      </motion.button>
    </div>
  )
}
